// English copy — the source catalog. Every key here must exist in th.ts
// (test/i18n.test.ts). Whole phrases only: a sentence that carries a value
// takes it as a {placeholder}, never split around a JSX expression.
export const en = {
  "app.name": "Broker Portal",
  "language.label": "Language",
  "language.english": "English",
  "language.thai": "Thai",

  // --- common ---
  "common.loading": "Loading…",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.done": "Done",
  "common.clear": "Clear",
  "common.search": "Search",
  "common.all": "All",
  "common.yes": "Yes",
  "common.no": "No",
  "common.status": "Status",
  "common.actions": "Actions",
  "common.when": "When",
  "common.reason": "Reason",
  "common.reasonRequired": "Reason (required)",
  "common.reasonOptional": "Reason (optional)",
  "common.reasonMissing": "Give a reason — it is recorded in the audit trail.",
  "common.reasonHint": "Recorded with the change in the broker audit trail.",
  "common.reload": "Reload",
  "common.loadMore": "Load more",
  "common.prev": "Previous",
  "common.next": "Next",
  "common.pagination": "Pagination",
  "common.range": "{from}–{to} of {total}",
  "common.pageNumber": "Page {page}",
  "common.asOf": "As of",

  // --- errors / guards ---
  "error.pageTitle": "Something went wrong",
  "error.serverSaid": "The server said: {message}",
  "error.requestId": "Request id: {id}",
  "error.rule": "Rule",
  "dialog.unknownOutcome": "The outcome is unknown: the server may or may not have saved this change. Refresh the page and check before trying again — sending it again could save it twice.",
  "dialog.refreshPage": "Refresh page",
  "guard.title": "Not available for your role",
  "guard.body": "This screen needs the {capability} capability on the selected node. The server's answer is below.",
  "notFound.title": "Page not found",
  "notFound.body": "There is no portal screen at this address.",
  "notFound.home": "Go to the overview",

  // --- capabilities / roles / statuses ---
  "capability.VIEW": "View",
  "capability.FINANCE_VIEW": "Finance view",
  "capability.STAFF_MANAGE": "Manage staff",
  "capability.CHILD_CREATE": "Create sub-brokers",
  "capability.ACCOUNT_PAUSE": "Pause accounts",
  "capability.FUNDING_REQUEST": "Request funding transfers",
  "capability.FEE_MANAGE": "Manage fee and revenue-share plans",
  "capability.BRANDING_MANAGE": "Change the currency image",
  "role.OWNER": "Owner",
  "role.ADMINISTRATOR": "Administrator",
  "role.FINANCE": "Finance",
  "role.TRADER": "Trader",
  "role.VIEWER": "Viewer",
  "membershipStatus.INVITED": "Invited",
  "membershipStatus.ACTIVE": "Active",
  "membershipStatus.SUSPENDED": "Suspended",
  "membershipStatus.REMOVED": "Removed",
  "brokerStatus.DRAFT": "Draft",
  "brokerStatus.ACTIVE": "Active",
  "brokerStatus.PAUSED": "Paused",
  "brokerStatus.CLOSING": "Closing",
  "brokerStatus.CLOSED": "Closed",
  "status.explain.DRAFT": "Awaiting the exchange's approval. A draft node takes no users, funding or orders until it is approved.",
  "status.explain.ACTIVE": "Fully operational: users can be served, funded and trade, subject to the exchange's limits.",
  "status.explain.PAUSED": "New exposure is stopped — no new orders, allocations or accounts. Risk-reducing actions (cancels, position reductions, returns, settlement and payouts) continue. Only the exchange can resume it.",
  "status.explain.CLOSING": "Winding down: nothing new starts, and open obligations are being cleared before the exchange closes the node.",
  "status.explain.CLOSED": "Closed. Its records stay readable for history; nothing can change on it.",
  "accountStatus.ACTIVE": "Active",
  "accountStatus.SUSPENDED": "Suspended",
  "accountStatus.CLOSED": "Closed",
  "balanceMode.SYSTEM_BROKER": "System broker",
  "balanceMode.BROKER_FUNDED": "Broker funded",
  "balanceMode.THIRD_PARTY_CREDIT": "Third-party credit",
  "entryPath.REGISTRATION": "Registration",
  "entryPath.SOCIAL_REGISTRATION": "Social registration",
  "entryPath.LOGIN_RECOVERY": "Login recovery",
  "entryPath.PROVIDER_LAUNCH": "Provider launch",
  "entryPath.BACKFILL": "Backfill",
  "positionStatus.OPEN": "Open",
  "positionStatus.CLOSED": "Closed",
  "positionStatus.RESOLVED": "Resolved",
  "positionStatus.REDEEMABLE": "Redeemable",
  "positionStatus.REDEEMED": "Redeemed",
  "positionStatus.LOST": "Lost",
  "positionStatus.REFUNDED": "Refunded",
  "loginMethod.PASSWORD": "Password",
  "loginMethod.MFA": "Two-factor code",
  "loginMethod.GOOGLE": "Google",
  "loginMethod.EMAIL_CONFIRM": "Email confirmation",
  "loginMethod.PROVIDER_LAUNCH": "Provider launch",
  "orderChannel.WEB": "Web",
  "orderChannel.EXTERNAL_LAUNCH": "Provider launch",
  "orderChannel.OPENAPI": "Open API",
  "orderSide.BUY": "Buy",
  "orderSide.SELL": "Sell",
  "orderStatus.NEW": "New",
  "orderStatus.RESERVED": "Reserved",
  "orderStatus.PENDING_ENGINE": "Pending engine",
  "orderStatus.PENDING_FUNDING": "Pending funding",
  "orderStatus.OPEN": "Open",
  "orderStatus.PARTIALLY_FILLED": "Partially filled",
  "orderStatus.CANCEL_PENDING": "Cancel pending",
  "orderStatus.FILLED": "Filled",
  "orderStatus.CANCELED": "Canceled",
  "orderStatus.REJECTED": "Rejected",
  "orderStatus.EXPIRED": "Expired",
  "orderStatus.FAILED": "Failed",
  "tif.GTC": "Good till canceled",
  "tif.GTD": "Good till date",
  "tif.FOK": "Fill or kill",
  "tif.FAK": "Fill and kill",
  "liquidity.MAKER": "Maker",
  "liquidity.TAKER": "Taker",
  "ledgerStatus.PENDING": "Pending",
  "ledgerStatus.COMMITTED": "Committed",
  "ledgerStatus.FAILED": "Failed",
  "fundingRole.MASTER": "Master",
  "fundingRole.POOL": "Pool",
  "transferKind.POOL_ALLOCATION": "Pool allocation",
  "transferKind.POOL_RETURN": "Pool return",
  "transferKind.USER_ALLOCATION": "User allocation",
  "transferKind.USER_RETURN": "User return",
  "transferState.PENDING": "Pending",
  "transferState.COMMITTED": "Committed",
  "transferState.FAILED": "Failed",
  "transferState.MANUAL_REVIEW": "Manual review",
  "actorKind.BROKER_CREDENTIAL": "Server credential",
  "actorKind.EXCHANGE_ADMIN": "Exchange",
  "actorKind.BROKER_STAFF": "Staff",
  "actorKind.SYSTEM": "System",
  "actor.exchange": "The exchange",
  "earningState.PENDING": "Pending",
  "earningState.AVAILABLE": "Available",
  "earningState.PAYOUT_RESERVED": "Reserved for payout",
  "earningState.PAID": "Paid",
  "earningState.VOID": "Void",
  "earningState.REASSIGNED": "Reassigned",
  "earningOrigin.EARNED": "Earned",
  "earningOrigin.ADJUSTMENT": "Adjustment",
  "earningOrigin.REASSIGNMENT": "Reassignment",
  "revenueRole.maker": "Maker",
  "revenueRole.taker": "Taker",
  "revenueRole.redemption": "Redemption",
  "reconState.RUNNING": "Running",
  "reconState.MATCHED": "Matched",
  "reconState.MISMATCH": "Mismatch",
  "reconState.FAILED": "Failed",
  "fundingRecon.OK": "Balanced",
  "fundingRecon.FINDINGS": "Findings",
  "fundingRecon.FAILED": "Failed",
  "schedule.WEEKLY": "Weekly",
  "schedule.MONTHLY": "Monthly",
  "schedule.MANUAL": "Manual batch",
  "feeScope.GROUP": "Whole group",
  "feeScope.BRANCH": "Branch",
  "feeScope.NODE": "This node only",
  "statementRow.EARNING": "Earning",
  "statementRow.REASSIGNED_OUT": "Reassigned out",
  "statementRow.PAYOUT": "Payout",
  "clearing.direction.WE_OWE_BROKER": "On the ledger, the exchange owes this broker (its users net won).",
  "clearing.direction.BROKER_OWES_US": "On the ledger, this broker owes the exchange (its users consumed more than was credited back).",
  "clearing.direction.FLAT": "Flat on the ledger: nothing either way.",
  "clearing.owed": "Ledger deficit, before settlements:",
  "clearing.none": "No clearing account — this node services no third-party-credit accounts.",
  "coverage.node": "This node only",
  "coverage.subtree": "Includes descendants",
  "coverage.split": "Own and descendants shown separately",

  // --- shell ---
  "nav.aria": "Portal navigation",
  "nav.version": "Broker portal · SM-621",
  "nav.inheritedAccess": "Access via a parent node",
  "nav.section.overview": "Overview",
  "nav.section.organization": "Organization",
  "nav.section.trading": "Trading",
  "nav.section.money": "Money",
  "nav.overview": "Overview",
  "nav.organization": "Organization",
  "nav.hierarchy": "Hierarchy",
  "nav.staff": "Staff",
  "nav.users": "Users",
  "nav.positions": "Active positions",
  "nav.orders": "Active orders",
  "nav.orderHistory": "Order history",
  "nav.trades": "Trade history",
  "nav.risk": "Risk",
  "nav.fees": "Fees",
  "nav.feePlans": "Plan management",
  "nav.funding": "Funding",
  "nav.revenue": "Revenue",
  "nav.payouts": "Payouts",
  "nav.statements": "Statements",
  "nav.reconciliation": "Reconciliation",
  "nav.settlement": "Settlement",
  "topbar.openNavigation": "Open navigation",
  "topbar.skipToContent": "Skip to content",
  "topbar.currency": "Currency",
  "topbar.currencyOnly": "{currency} is the only currency this node trades",
  "topbar.account": "Account menu",
  "topbar.brokerStaff": "Broker staff",
  "topbar.signOut": "Sign out",
  "topbar.signingOut": "Signing out…",
  "topbar.security": "Security",
  "node.label": "Node",
  "node.choose": "Choose a node",
  "node.breadcrumb": "Path to this node",
  "node.search": "Search nodes",
  "node.searchPlaceholder": "Name or broker id",
  "node.results": "Results",
  "node.noResults": "No node in your scope matches.",
  "node.memberships": "Your memberships",
  "node.noMemberships": "No active membership.",
  "node.children": "Direct children",
  "node.noChildren": "This node has no children.",
  "node.childCount": "Children: {count}",
  "node.selectorAria": "Selected node: {name}. Choose another node",
  "node.loadFailed": "Could not load nodes.",
  "header.node": "Node: {name}",
  "header.currency": "Figures in",
  "shell.signedInAs": "Signed in as {email}",
  "shell.notStaff.eyebrow": "Broker portal",
  "shell.notStaff.title": "This account is not broker staff",
  "shell.notStaff.body": "You are signed in, but you hold no membership on any broker. Ask a broker owner or administrator to invite you, then sign in again.",
  "shell.noActive.eyebrow": "Broker portal",
  "shell.noActive.invitedTitle": "You have been invited",
  "shell.noActive.invitedBody": "Accept an invitation to open that broker's portal. Declining asks for a reason, which is recorded.",
  "shell.noActive.suspendedTitle": "Your broker access is suspended",
  "shell.noActive.suspendedBody": "Every membership you hold is suspended. An owner or administrator of the broker can resume it.",
  "shell.noActive.suspendedCount": "{count} suspended membership(s).",
  "shell.nodeError.eyebrow": "Broker portal",
  "shell.nodeError.title": "That node is not in your scope",
  "shell.nodeError.body": "The link names node {id}. Your memberships do not cover it, or it does not exist. The server's answer is below.",
  "shell.nodeError.home": "Go to your node",
  "shell.error.title": "The portal could not load your memberships",

  // --- invitations ---
  "invitations.title": "Invitations",
  "invitations.subtitle": "Memberships you have been invited to on other nodes.",
  "invitations.pending": "Pending invitations",
  "invitations.none": "No pending invitations.",
  "invitations.banner": "You have {count} pending invitation(s) to another node.",
  "invitations.review": "Review",
  "invitations.line": "{role} on {id}",
  "invitations.invitedAt": "Invited",
  "invitations.accept": "Accept",
  "invitations.decline": "Decline",
  "invitations.acceptTitle": "Accept the invitation to {name}?",
  "invitations.acceptBody": "You will join as {role}. The membership takes effect immediately.",
  "invitations.declineTitle": "Decline the invitation to {name}?",
  "invitations.declineBody": "The invitation is removed. A new one is needed to join later.",

  // --- login ---
  "login.brand": "Smart Market · Broker portal",
  "login.eyebrow": "Broker staff",
  "login.signIn": "Sign in to the broker portal",
  "login.subtitle": "Use the email your broker invited. What you can see depends on your memberships.",
  "login.email": "Email",
  "login.password": "Password",
  "login.continue": "Continue",
  "login.signingIn": "Signing in…",
  "login.twoFactor": "Two-factor authentication",
  "login.enterCode": "Enter your code",
  "login.codeInstruction": "Enter the 6-digit code from your authenticator app for {email}.",
  "login.digitAria": "Digit {digit}",
  "login.verify": "Verify",
  "login.verifying": "Verifying…",
  "login.back": "Back to sign in",
  "login.error.credentials": "The email or password is not correct.",
  "login.error.codeRejected": "That code was not accepted. Try the current code.",
  "login.error.rateLimit": "Too many attempts. Wait a moment and try again.",
  "login.error.unreachable": "The sign-in service could not be reached.",
  "login.error.verify": "The code could not be verified.",
  "login.error.signIn": "Sign-in failed.",
  "login.error.noFactor": "This account needs a second factor, but none is set up.",
  "login.reason.expired": "Your session ended. Sign in again to continue.",
  "login.reason.signed-out": "You have signed out.",
  "login.reason.revoked": "Your broker access was removed or suspended, so you were signed out.",
  "login.forgot": "Forgot password?",
  "forgot.title": "Reset your password",
  "forgot.subtitle": "Enter the email you sign in to the broker portal with. We'll email you a link to choose a new password.",
  "forgot.submit": "Email me a link",
  "forgot.sent": "If that email has a broker portal sign-in, we've sent a link to it. The link works once and expires in 1 hour.",
  "password.set.title": "Set your password",
  "password.set.subtitle": "Choose a password for your broker portal sign-in. You'll be signed in right after.",
  "password.set.submit": "Set password and sign in",
  "password.reset.title": "Choose a new password",
  "password.reset.subtitle": "Your new password replaces the old one, and you'll be signed out everywhere else.",
  "password.reset.submit": "Save new password",
  "password.new": "New password",
  "password.confirm": "Confirm the new password",
  "password.rule": "At least {min} characters.",
  "password.tooShort": "Use at least {min} characters.",
  "password.mismatch": "The two passwords do not match.",
  "password.saving": "Saving…",
  "password.error.failed": "The password could not be saved. Try again.",
  "password.expired.title": "This link has expired",
  "password.expired.set": "This link has expired or was already used. Ask the person who invited you to send a new one.",
  "password.expired.reset": "This link has expired or was already used. Request a new one.",
  "password.expired.requestNew": "Request a new link",

  // --- audit ---
  "audit.recorded": "Done — recorded in the audit trail.",
  "audit.reference": "Audit reference:",
  "audit.referenceMissing": "The server did not return an audit reference for this action.",

  // --- overview ---
  "overview.title": "Overview",
  "overview.subtitle": "Six panels from one read. Each says whether its source answered.",
  "overview.descendantCount": "{count} descendant nodes in scope",
  "overview.leaf": "No descendant nodes",
  "overview.generatedAt": "Generated",
  "overview.panel.earnings": "Earnings",
  "overview.panel.revenue": "Revenue position",
  "overview.panel.funding": "Funding",
  "overview.panel.exposure": "Exposure",
  "overview.panel.activity": "Activity",
  "overview.panel.attention": "Needs attention",
  "overview.empty.earnings": "No earnings in this currency yet.",
  "overview.empty.revenue": "No revenue in this currency yet.",
  "overview.empty.funding": "No pool in this currency.",
  "overview.empty.exposure": "No exposure: exposure exists only for third-party-credit accounts in GOLD.",
  "overview.empty.activity": "No activity in this currency this period.",
  "overview.empty.attention": "Nothing needs attention.",
  "overview.forbidden": "Your role cannot see this panel (it needs Finance view).",
  "overview.unavailable": "The source did not answer; no figures are shown rather than wrong ones.",
  "overview.unknownState": "This panel reported a state the portal does not know: {state}.",
  "overview.stale": "Stale",
  "overview.asOf": "Source answered",
  "overview.openDetail": "Open {screen}",
  "overview.figure": "Figure",
  "overview.own": "This node",
  "overview.descendants": "Descendants",
  "overview.total": "Total",
  "overview.totalSub": "This node + descendants",
  "overview.earnings.allTime": "All-time fee",
  "overview.earnings.today": "Today fee",
  "overview.earnings.period": "This period fee",
  "overview.earnings.previous": "Previous period fee",
  "overview.earnings.window": "Current period",
  "overview.activity.activeAccounts": "Active accounts",
  "overview.activity.openOrders": "Open orders",
  "overview.activity.openPositions": "Open positions",
  "overview.activity.fills": "Fills this period",
  "overview.activity.tradedValue": "Traded value this period",
  "overview.activity.partial": "Some figures are missing because a source did not answer ({code}).",
  "overview.activity.figureUnavailable": "Unavailable",
  "overview.attention.none": "Nothing needs attention.",
  "attention.RECON_FINDINGS": "{count} reconciliation finding(s)",
  "attention.FAILED_PAYOUTS": "{count} failed payout batch(es)",
  "attention.PAUSED_ACCOUNTS": "{count} suspended user account(s)",
  "attention.PAUSED_NODES": "{count} paused node(s)",
  "attention.PENDING_INVITATIONS": "{count} pending staff invitation(s)",
  "attention.EXPOSURE_WARNING": "Exposure is near or over its limit",

  // --- organization ---
  "org.title": "Organization",
  "org.subtitle": "Profile, business settings and lifecycle status. Read-only in the portal, except the currency image.",
  "org.status.title": "Lifecycle status",
  "org.status.own": "Own status",
  "org.status.effective": "Effective status",
  "org.status.restricted": "A node above this one restricts it: its effective status is not its own.",
  "org.status.reason": "Reason: {reason}",
  "org.status.changedAt": "Changed",
  "org.exchangeOperated": "Profile, settings and lifecycle changes are made by the exchange. Contact the exchange to request one.",
  "org.profile.title": "Profile",
  "org.profile.displayName": "Display name",
  "org.profile.legalName": "Legal name",
  "org.profile.brokerId": "Broker id",
  "org.profile.parent": "Parent",
  "org.profile.isRoot": "None — this is a root broker",
  "org.profile.root": "Root broker",
  "org.profile.depth": "Depth",
  "org.profile.settingsVersion": "Settings version",
  "org.profile.hierarchyVersion": "Hierarchy version",
  "org.profile.created": "Created",
  "org.profile.updated": "Updated",
  "org.contacts": "Contacts",
  "org.noContacts": "No contacts recorded.",
  "org.settings": "Business settings",
  "org.noSettings": "No business settings recorded.",
  "org.audit.title": "Audit trail",
  "org.audit.empty": "No audit events yet.",
  "org.audit.action": "Action",
  "org.audit.entity": "Entity",
  "org.audit.actor": "Actor",
  "org.audit.event": "Event",
  "org.image.title": "Currency image (GOLD)",
  "org.image.intro": "The coin your users see next to every GOLD amount on the website. Only the image changes: the name, symbol and decimals stay the platform's.",
  "org.image.source": "Shown to your users",
  "org.image.source.own": "Your image",
  "org.image.source.parent": "From parent broker {name}",
  "org.image.source.parentHidden": "From a parent broker",
  "org.image.source.default": "Gold (platform default)",
  "org.image.sample": "How an amount looks",
  "org.image.changedAt": "Last changed",
  "org.image.changedBy": "Changed by",
  "org.image.upload": "Upload image",
  "org.image.replace": "Replace image",
  "org.image.remove": "Remove image",
  "org.image.hint": "Square image, at least 128 × 128. It is shown small, next to every amount.",
  "org.image.types": "PNG, JPEG, WebP or SVG, up to 10 MB.",
  "org.image.preview": "Preview before you save",
  "org.image.previewSize": "{px} px",
  "org.image.save": "Save image",
  "org.image.saved": "Saved. Your users see the new image within 5 minutes, or when they reload the page.",
  "org.image.removeTitle": "Remove the currency image?",
  "org.image.removeToParent": "Your users will see {name}'s image.",
  "org.image.removeToParentHidden": "Your users will see your parent broker's image.",
  "org.image.removeToGold": "Your users will see the Gold coin.",
  "org.image.lock.system": "The SYSTEM_BROKER's users always see the platform's Gold coin, so this image cannot be changed.",
  "org.image.lock.closed": "This broker is closed, so its currency image can no longer be changed.",
  "org.image.readOnly": "Only an owner or administrator of this broker, or of a broker above it, can change this image.",
  "org.image.err.tooLarge": "This file is too large. Choose an image of 10 MB or less.",
  "org.image.err.type": "This is not an image we accept. Choose a PNG, JPEG, WebP or SVG file.",

  // --- staff ---
  "staff.title": "Staff",
  "staff.subtitle": "Members of this node and their roles.",
  "staff.members": "Members",
  "staff.includeRemoved": "Include removed",
  "staff.empty": "No staff on this node.",
  "staff.readOnly": "You can see this node's staff. Managing them needs an owner or administrator membership on this node or on a broker above it.",
  "staff.user": "User",
  "staff.role": "Role",
  "staff.invited": "Invited",
  "staff.activated": "Activated",
  "staff.by": "by {id}",
  "staff.byExchange": "by the exchange",
  "staff.you": "You",
  "staff.invite": "Invite",
  "staff.inviteTitle": "Invite a staff member",
  "staff.inviteBody": "We'll email the person a link. They accept the invitation themselves after signing in.",
  "staff.roleHint": "You can grant at most your own role; the server refuses anything higher.",
  "staff.changeRole": "Change role",
  "staff.roleTitle": "Change the role of {user}",
  "staff.roleBody": "Current role: {role}.",
  "staff.newRole": "New role",
  "staff.cmd.suspend.title": "Suspend {user}?",
  "staff.cmd.suspend.body": "The member loses access on their next request until resumed.",
  "staff.cmd.suspend.confirm": "Suspend",
  "staff.cmd.resume.title": "Resume {user}?",
  "staff.cmd.resume.body": "The member regains the access of their role.",
  "staff.cmd.resume.confirm": "Resume",
  "staff.cmd.remove.title": "Remove {user}?",
  "staff.cmd.remove.body": "Removal is final. A new invitation is needed to add them again.",
  "staff.cmd.remove.confirm": "Remove",
  "staff.coverage": "Coverage",
  "staff.thisNode": "This node",
  "staff.includeSubBrokers": "Include sub-brokers",
  "staff.coverageSubtree": "This node + sub-brokers",
  "staff.emptySubtree": "No staff on this node or its sub-brokers yet.",
  "staff.node": "Node",
  "staff.openNode": "Open this node",
  "staff.filterUser": "Show the nodes this person can reach",
  "staff.dates": "Invited / activated",
  "staff.accessFor": "Access for",
  "staff.addNodeAccess": "Add node access",
  "staff.clearFilter": "Clear",
  "staff.assignedByParent": "Assigned by parent broker",
  "staff.assignedByParentHint": "A broker above this node placed this member. This node's owners and administrators can still change or remove them.",
  "staff.inviteSubtreeBody": "We'll email the person a link to set a password and sign in to the broker portal. They get access to each selected node and everything under it, never the nodes above or beside it, and accept each invitation themselves.",
  "staff.inviteReasonHint": "Recorded in the audit trail of each selected node.",
  "staff.roleHintOwner": "As an owner you can grant any role.",
  "staff.roleHintAdmin": "As an administrator you can grant Finance, Trader and Viewer.",
  "staff.selfInvite": "You cannot invite yourself. Ask another owner or administrator.",
  "staff.nodes": "Nodes",
  "staff.nodesSearch": "Search nodes by name or id",
  "staff.nodesNone": "No nodes match.",
  "staff.nodesHint": "The person gets access to each selected node and everything under it. They will not see nodes above or beside it.",
  "staff.nodesSelected": "Nodes selected: {count}",
  "staff.inviteToNodes": "Invite to {count} nodes",
  "staff.inviteResultTitle": "Invitation results",
  "staff.inviteResultBody": "{ok} of {total} invitations were sent.",
  "staff.inviteState.pending": "Sending",
  "staff.inviteState.ok": "Invited",
  "staff.inviteState.failed": "Failed",
  "staff.retryFailed": "Retry failed ({count})",
  "staff.email": "Email",
  "staff.emailPlaceholder": "name@company.com",
  "staff.emailHint": "We'll email them a link to set a password and sign in to the broker portal. Until they accept, the invitation grants nothing.",
  "staff.emailInvalid": "Enter a valid email address, like name@company.com.",
  "staff.pendingSignUp": "Pending sign-up",
  "staff.pendingSignUpHint": "Invited, but has not set a password yet. You can send the invitation email again.",
  "staff.technicalDetails": "Technical details",
  "staff.staffId": "Staff id",
  "staff.resend.title": "Send the invitation to {user} again?",
  "staff.resend.body": "We'll email a new link to set a password to {email}. The link in the earlier email stops working.",
  "staff.resend.confirm": "Resend invite",
  "security.title": "Security",
  "security.subtitle": "Your broker portal sign-in: password and authenticator app.",
  "security.clientMode": "You sign in to the broker portal with your trading-site account, so change its password and two-step sign-in there.",
  "security.account": "Your sign-in",
  "security.name": "Name",
  "security.passwordChanged": "Password last changed",
  "security.changePassword": "Change password",
  "security.currentPassword": "Current password",
  "security.changePasswordHint": "Changing your password signs you out on every other device.",
  "security.passwordChangedDone": "Password changed. You were signed out on your other devices.",
  "security.authenticator": "Authenticator app",
  "security.totpOn": "On",
  "security.totpOff": "Off",
  "security.totpOffBody": "Add a second step to your sign-in: after your password, you enter a 6-digit code from an authenticator app on your phone.",
  "security.totpSetUp": "Set up authenticator app",
  "security.qrAria": "QR code to scan with your authenticator app",
  "security.totpStep1": "Open your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) and scan this QR code.",
  "security.totpStep2": "Enter the 6-digit code the app shows to finish.",
  "security.totpManual": "Can't scan? Enter this key in the app:",
  "security.code": "6-digit code",
  "security.totpConfirm": "Turn on",
  "security.totpOnDone": "Authenticator app is on. You'll enter a code from it each time you sign in.",
  "security.totpOnBody": "You enter a code from your authenticator app each time you sign in. To remove it, enter a current code.",
  "security.totpRemove": "Remove authenticator app",
  "security.totpOffDone": "Authenticator app removed. You sign in with your password only.",
  "security.totpFailed": "That did not work. Try again.",
  "child.open": "Create sub-broker",
  "child.title": "Create a sub-broker",
  "child.body": "The new node is created under {parent} in DRAFT.",
  "child.displayName": "Display name",
  "child.legalName": "Legal name",
  "child.draftNote": "A draft node does nothing until the exchange approves it: no users, funding or orders.",
  "child.create": "Create draft",
  "child.keyConflict": "This form was already sent with different details. Close it and start again.",

  // --- hierarchy ---
  "hierarchy.title": "Hierarchy",
  "hierarchy.subtitle": "The nodes under the selected node. Open any row to make it the selected node.",
  "hierarchy.search": "Find a node",
  "hierarchy.searchLabel": "Search this subtree",
  "hierarchy.tree": "Under {name}",
  "hierarchy.treeAria": "Node tree",
  "hierarchy.noChildren": "This node has no children.",
  "hierarchy.noChildrenBelow": "No children.",
  "hierarchy.loadMore": "Load more ({shown} of {total})",
  "hierarchy.expand": "Expand {name}",
  "hierarchy.collapse": "Collapse {name}",
  "hierarchy.effective": "Effectively {status}",
  "hierarchy.effectiveHint": "A node above restricts this one.",
  "hierarchy.open": "Open",

  // --- users ---
  "users.title": "Users",
  "users.subtitle": "Broker accounts this node and its descendants service. Scope narrows it to this node's own.",
  "users.filters": "Filters",
  "users.scope": "Scope",
  "users.scopeDirect": "Serviced directly",
  "users.scopeSubtree": "Whole subtree",
  "users.balanceMode": "Balance mode",
  "users.search": "Search",
  "users.searchPlaceholder": "User id or account id prefix",
  "users.accounts": "Accounts",
  "users.empty": "No accounts match.",
  "users.account": "Account",
  "users.user": "User",
  "users.servicingNode": "Servicing node",
  "users.thisNode": "This node",
  "users.created": "Created",
  "users.updated": "Updated",
  "users.details": "Details",
  "users.detailTitle": "User",
  "users.accountCard": "Account",
  "users.openInNewTab": "Open account {id} in a new tab",
  "users.brokerEffective": "Servicing broker status",
  "users.entryPath": "Entry path",
  "users.statusReason": "Status reason",
  "users.tabs": "Account activity",
  "users.tab.orders": "Active orders",
  "users.tab.positions": "Active positions",
  "users.tab.history": "Order history",
  "users.tab.trades": "Trade history",
  "users.eventsTitle": "History",
  "users.noEvents": "No history yet.",
  "users.eventActor": "By {kind} {id}",
  "users.eventActorExchange": "By the exchange",
  "users.eventReason": "Reason: {reason}",
  "users.closedNote": "This account is closed. Closing is an exchange action and cannot be undone here.",
  "users.pauseNeedsRole": "Pausing an account needs an owner or administrator membership on the node that services it.",
  "users.pauseDirectOnly": "Only the node that services this account directly can pause it.",
  "users.pauseNote": "Pausing never closes the account and never touches its balance.",
  "users.cmd.suspend.title": "Suspend account {id}?",
  "users.cmd.suspend.body": "The user cannot trade through this account until it is resumed. Open positions and balances are untouched.",
  "users.cmd.suspend.confirm": "Suspend account",
  "users.cmd.resume.title": "Resume account {id}?",
  "users.cmd.resume.body": "The user can use this account again.",
  "users.cmd.resume.confirm": "Resume account",
  "users.loginLogTitle": "Login log",
  "users.loginLogEmpty": "No logins recorded for this account.",
  "users.loginLogHint": "Logins made while this account was open. None are listed while the user also has an open account with a broker outside this subtree.",
  "users.orderLogTitle": "Order log",
  "users.orderLogEmpty": "No orders for this account.",
  "users.orderLogHint": "Orders placed before broker attribution are not listed.",
  "users.orderLogUnrecordedNote": "A dash for IP, device and channel means the order's origin was not recorded, for example because it was placed before origin logging was switched on.",
  "clientLog.time": "Time",
  "clientLog.method": "Method",
  "clientLog.ip": "IP",
  "clientLog.device": "Device",
  "clientLog.market": "Market",
  "clientLog.side": "Side",
  "clientLog.channel": "Channel",
  "device.label": "{browser} · {os} · {form}",
  "device.mobile": "Mobile",
  "device.desktop": "Desktop",
  "device.unknownBrowser": "Unknown browser",
  "device.unknownOs": "Unknown OS",

  // --- positions ---
  "positions.title": "Positions",
  "positions.subtitle": "Positions of the users this node and its descendants service, read from settlement.",
  "positions.list": "Positions",
  "positions.empty": "No positions in this currency.",
  "positions.market": "Market",
  "positions.outcome": "Outcome",
  "positions.account": "Account",
  "positions.node": "Node {id}",
  "positions.quantity": "Shares",
  "positions.avgPrice": "Average price",
  "positions.realizedPnl": "Realized P&L",
  "positions.opened": "Opened",
  "positions.emptyActive": "No active positions in this currency.",
  "positions.statusActive": "Active (open or redeemable)",

  // --- orders and trades (SM-823) ---
  "orders.title": "Active orders",
  "orders.subtitle": "Working orders of the users this node and its descendants service. Read-only.",
  "orderHistory.title": "Order history",
  "orderHistory.subtitle": "One row per finished order: filled, canceled, rejected, expired or failed.",
  "trades.title": "Trade history",
  "trades.subtitle": "One row per side of each fill, with its fees and whether the ledger has settled it.",
  "orders.activeList": "Active orders",
  "orders.historyList": "Order history",
  "orders.placed": "Placed",
  "orders.finished": "Finished",
  "orders.market": "Market",
  "orders.outcome": "Outcome",
  "orders.account": "Account",
  "orders.side": "Side",
  "orders.price": "Price",
  "orders.quantity": "Quantity",
  "orders.filled": "Filled",
  "orders.tif": "Time in force",
  "orders.reason": "Reason",
  "orders.emptyActive": "No active orders in this currency.",
  "orders.emptyHistory": "No finished orders in this currency.",
  "trades.list": "Trade history",
  "trades.time": "Time",
  "trades.liquidity": "Maker / taker",
  "trades.notional": "Notional",
  "trades.exchangeFee": "Exchange fee",
  "trades.brokerFee": "Broker commission",
  "trades.net": "Net",
  "trades.ledger": "Ledger",
  "trades.empty": "No trades in this currency.",
  "trades.gameSellsNote": "GAME sells settle in GREEN.",
  "trading.emptyHintAccount": "Only activity while this user is under your broker is shown.",
  "trading.emptyHintNode": "Only activity while users are under your broker is shown.",
  "trading.user": "User",
  "trading.userResults": "Matching accounts",
  "trading.userSearchFailed": "Could not search the accounts.",
  "trading.userClear": "Show all users",
  "trading.openAccount": "Open account {id} on the Users screen in a new tab",
  "trading.period": "Period",
  "trading.lastDays": "Last {days} days",
  "trading.rangeTooLong": "Choose at most {days} days.",
  "trading.window": "Window: {from} to {to} inclusive, whole days in your local time",

  // --- risk ---
  "risk.title": "Risk",
  "risk.subtitle": "Exposure, collateral and limit. Read-only: the exchange sets limits.",
  "risk.goldOnly": "Only GOLD is shown: exposure is third-party credit, which exists in GOLD only.",
  "risk.level.blocked": "Blocked: new exposure is refused.",
  "risk.level.warning": "Warning: utilisation has reached the warning threshold.",
  "risk.level.ok": "Within limit.",
  "risk.level.no-limit": "This node has no limit of its own; the nearest limited node above applies.",
  "risk.blocked": "Blocked: new exposure is refused.",
  "risk.warning": "Near the limit: utilisation is at or above the warning threshold.",
  "risk.blockedBecause": "Because: {reasons}",
  "risk.blockReason.HEADROOM_EXHAUSTED": "headroom exhausted",
  "risk.blockReason.RECEIVABLE_OPEN": "an open receivable",
  "risk.blockReason.LIFECYCLE": "the node's lifecycle status",
  "risk.ownExposure": "Own exposure",
  "risk.ownExposureSub": "Accounts this node services",
  "risk.subtreeExposure": "Subtree exposure",
  "risk.subtreeExposureSub": "This node and every descendant",
  "risk.openEntriesCount": "Open orders and positions: {count}",
  "risk.collateral": "Collateral",
  "risk.collateralSub": "Pools across the subtree",
  "risk.effectiveHeadroom": "Effective headroom",
  "risk.bindingNode": "Bound by {name}",
  "risk.bindingSelf": "Bound by this node's own limit",
  "risk.bindingAbove": "Bound by a node above your access",
  "risk.limitTitle": "Limit",
  "risk.limit": "Limit",
  "risk.headroom": "Headroom",
  "risk.allowance": "Allowance",
  "risk.allowanceExpires": "Allowance expires",
  "risk.utilisation": "Utilisation",
  "risk.utilisationOf": "{used} used · warning at {warn}",
  "risk.noOwnLimit": "No own limit",
  "risk.noOwnLimitBody": "This node has no limit of its own. The smallest headroom of a limited node on its path applies (effective headroom above).",
  "risk.settlementTitle": "Net position and receivables",
  "risk.netPosition": "Net position",
  "risk.owed": "Owed to the exchange (whole tree)",
  "risk.owedHint": "What your tree's trades consumed beyond what was credited back. Settlement notes and outside payments never lower it; a receivable the exchange marks paid or written off is subtracted below instead.",
  "risk.coveredByResolutions": "Paid or written off",
  "risk.coveredByResolutionsHint": "Receivables the exchange closed as paid outside the platform or written off.",
  "risk.uncovered": "Collateral shortfall",
  "risk.uncoveredHint": "Owed to the exchange minus collateral and paid or written-off receivables, never below 0. 0.00 means you are covered; above 0 opens a receivable that blocks new orders.",
  "risk.receivablesOpen": "Open receivables",
  "risk.netPositionNote": "Positive net position means the exchange owes the broker; figures are the root's.",

  // --- fees ---
  "fees.title": "Fees",
  "fees.subtitle": "The fee plan that applies to this node's users, and a calculator.",
  "fees.exchangeAuthored": "Fee plans and revenue-share plans are set by the exchange or your root broker. This screen shows what applies and prices examples with the server's own calculator.",
  "fees.rootManages": "You manage this tree's fee plans and revenue-share plans on its root node, {root}. This screen shows what applies here.",
  "fees.openPlanManagement": "Open plan management",
  "fees.noPlan": "No fee plan applies to this node in {currency}.",
  "fees.noPlanHint": "Orders from this node's users are refused until the exchange or the root broker assigns one.",
  "fees.assigned": "Assigned plan",
  "fees.plan": "Plan",
  "fees.active": "Active",
  "fees.retired": "Retired",
  "fees.version": "Version",
  "fees.versionN": "Version {n}",
  "fees.currency": "Currency",
  "fees.effective": "Effective",
  "fees.assignment": "Assigned at",
  "fees.assignmentTarget": "Assignment target",
  "fees.assignedFrom": "Assigned from",
  "fees.rates": "Rates",
  "fees.makerRate": "Maker rate",
  "fees.takerRate": "Taker rate",
  "fees.redemptionRate": "Redemption rate",
  "fees.nodeShare": "This node's share of the commission",
  "fees.shareVersion": "Revenue-share version",
  "fees.planVersionId": "Plan version id",
  "fees.shareNote": "The share is the revenue-share version that would lock for a new order now. A redemption takes the split in effect when it is redeemed.",
  "fees.defaultSplit": "Default split: 100% to the servicing broker",
  "fees.upcoming": "Scheduled changes",
  "fees.noUpcoming": "No scheduled changes.",
  "fees.change": "Change",
  "fees.upcomingAssignment": "New assignment ({scope}) to plan {plan}",
  "fees.upcomingCleared": "Assignment cleared ({scope})",
  "fees.calc.tradeTitle": "Trade fee calculator",
  "fees.calc.redemptionTitle": "Redemption fee calculator",
  "fees.calc.redemptionGoldOnly": "Redemption fees apply only to GOLD; other currencies price at zero.",
  "fees.calc.quantity": "Quantity (shares)",
  "fees.calc.quantityHint": "Up to 4 decimals.",
  "fees.calc.price": "Price",
  "fees.calc.priceHint": "Between 0 and 1, e.g. 0.55.",
  "fees.calc.role": "Role",
  "fees.calc.market": "Market id (optional)",
  "fees.calc.marketHint": "With a market, the exchange component is priced too.",
  "fees.calc.servicing": "Servicing node",
  "fees.calc.servicingHint": "The node whose user is trading: this node or a descendant.",
  "fees.calc.thisNode": "This node ({name})",
  "fees.calc.otherNode": "Or a deeper descendant's id",
  "fees.calc.otherNodePlaceholder": "brk_…",
  "fees.calc.run": "Calculate",
  "fees.calc.inputError.empty": "Enter a value.",
  "fees.calc.inputError.invalid": "Enter a number.",
  "fees.calc.inputError.tooPrecise": "Too many decimals.",
  "fees.calc.inputError.notPositive": "Must be more than zero.",
  "fees.calc.notional": "Notional",
  "fees.calc.feeBase": "Fee base",
  "fees.calc.feeBaseSub": "quantity × price × (1 − price)",
  "fees.calc.exchangeFee": "Exchange fee",
  "fees.calc.exchangeNeedsMarket": "Give a market id to price the exchange component.",
  "fees.calc.brokerCommission": "Broker commission",
  "fees.calc.atRate": "at {rate}",
  "fees.calc.totalUserFee": "Total user fee",
  "fees.calc.nodeShare": "This node's share",
  "fees.calc.shareOf": "{share} of the commission",
  "fees.calc.noShare": "No share for this node",
  "fees.calc.winningShares": "Winning shares redeemed",
  "fees.calc.gross": "Gross payout",
  "fees.calc.exchangeRedemptionFee": "Exchange redemption fee",
  "fees.calc.brokerRedemptionFee": "Broker redemption fee",
  "fees.calc.totalFee": "Total fee",
  "fees.calc.netPayout": "Net payout",
  "fees.calc.lockedPlan": "Plan version",
  "fees.calc.feeOwner": "Fee owner",

  // --- plan management (SM-844..846): the root's owners and finance staff ---
  "authoring.when.label": "Takes effect",
  "authoring.when.now": "Now",
  "authoring.when.later": "At a later time",
  "authoring.when.at": "Date and time (your local time)",
  "authoring.when.localTime": "Takes effect {time}, your local time.",
  "authoring.when.error.empty": "Choose a date and time.",
  "authoring.when.error.invalid": "Enter a valid date and time.",
  "authoring.when.error.past": "Choose a time in the future: changes are never backdated.",
  "feePlans.title": "Plan management",
  "feePlans.subtitle": "Fee plans and revenue-share plans for this root's whole tree. Only the root's owners and finance staff author them; every change is versioned and recorded in the audit trail.",
  "feePlans.rootOnlyTitle": "Plan management is on the root node",
  "feePlans.rootOnlyBody": "Fee plans and revenue-share plans belong to the root broker, {root}. Managing them is not passed down to the nodes below it, so switch to the root to open plan management.",
  "feePlans.switchToRoot": "Switch to {root}",
  "feePlans.rulesTitle": "How fee plans work",
  "feePlans.rule.currency": "A version covers one currency, and broker trees trade GOLD only, so new versions are published in GOLD. An older RED or GREEN version stays listed as history, marked Not traded.",
  "feePlans.rule.immutable": "Published versions and assignments are never edited. A change is a new version or a newer assignment with its own effective time, and a scheduled change never reprices an accepted order.",
  "feePlans.rule.ceiling": "Combined maker and combined taker rates (exchange plus broker) must each stay within 100%. This is checked when an order is accepted, not when you publish: a version above it makes every affected order fail.",
  "feePlans.rule.redemptionCeiling": "A GOLD broker redemption rate plus the exchange's highest redemption rate must stay within 100%. This is checked when you publish.",
  "feePlans.rule.noFallback": "Fee rules never fall back: when the plan a node resolves to is retired, or has no version in a currency, orders in it are refused, and so are GOLD redemptions (manual and auto-redeem), positions already held included.",
  "feePlans.newPlan": "New fee plan",
  "feePlans.list": "Fee plans",
  "feePlans.empty": "No fee plans yet.",
  "feePlans.emptyHint": "Create a plan, publish a version for each currency it covers, then assign it.",
  "feePlans.name": "Name",
  "feePlans.planId": "Plan id",
  "feePlans.created": "Created",
  "feePlans.open": "Open",
  "feePlans.detailTitle": "Fee plan: {name}",
  "feePlans.status.active": "Active",
  "feePlans.status.retiring": "Retires {time}",
  "feePlans.status.retired": "Retired",
  "feePlans.status.protected": "Protected",
  "feePlans.protectedBanner": "This plan is protected: it takes no new version and cannot be retired.",
  "feePlans.retiredBanner": "This plan is retired. Orders and GOLD redemptions (manual and auto-redeem) that resolve to it are refused until their scope is assigned another plan.",
  "feePlans.retiringBanner": "This plan retires at {time}. From then, orders and GOLD redemptions (manual and auto-redeem) that resolve to it are refused until their scope is assigned another plan.",
  "feePlans.publishVersion": "Publish version",
  "feePlans.retire": "Retire plan",
  "feePlans.versionsIn": "{currency} versions",
  "feePlans.noVersionsIn": "No {currency} version yet: orders in {currency} that resolve to this plan are refused.",
  "feePlans.col.version": "Version",
  "feePlans.col.maker": "Maker",
  "feePlans.col.taker": "Taker",
  "feePlans.col.redemption": "Redemption",
  "feePlans.col.effective": "Effective",
  "feePlans.col.published": "Published",
  "feePlans.versionStatus.current": "Current",
  "feePlans.versionStatus.scheduled": "Scheduled",
  "feePlans.versionStatus.superseded": "Superseded",
  "feePlans.versionStatus.retired": "Not applied: plan retired",
  "feePlans.versionStatus.notTraded": "Not traded",
  "feePlans.create.title": "New fee plan",
  "feePlans.create.body": "The plan starts empty: rates come with its versions, one currency at a time. Nothing applies until the plan is assigned.",
  "feePlans.create.name": "Plan name",
  "feePlans.create.nameHint": "Up to 200 characters.",
  "feePlans.create.confirm": "Create plan",
  "feePlans.publish.title": "Publish a version of {name}",
  "feePlans.publish.body": "A published version is never edited. Every node whose rule resolves to this plan uses it from its effective time.",
  "feePlans.publish.currency": "Currency",
  "feePlans.publish.current": "Current {currency} version: maker {maker}, taker {taker}, redemption {redemption}.",
  "feePlans.publish.noCurrent": "No {currency} version is in effect now.",
  "feePlans.publish.maker": "Maker rate (%)",
  "feePlans.publish.taker": "Taker rate (%)",
  "feePlans.publish.redemption": "Redemption rate (%)",
  "feePlans.publish.rateHint": "0 to 100, at most 4 decimals.",
  "feePlans.publish.redemptionGoldOnly": "Redemption fees are GOLD only: a {currency} version carries 0.",
  "feePlans.publish.rateError.empty": "Enter a rate.",
  "feePlans.publish.rateError.invalid": "Enter a number, with a point for decimals.",
  "feePlans.publish.rateError.tooPrecise": "At most 4 decimals.",
  "feePlans.publish.rateError.tooHigh": "At most 100%.",
  "feePlans.publish.ceiling": "Combined maker and combined taker rates (exchange plus broker) must each stay within 100%. Orders are checked when they are accepted, not now: a version above it makes every affected order fail.",
  "feePlans.publish.redemptionCeiling": "This redemption rate plus the exchange's highest GOLD redemption rate must stay within 100%; the server checks it now.",
  "feePlans.publish.redemptionPricing": "Maker and taker rates lock when an order is accepted. Redemption fees don't: a redemption is priced with the GOLD version in effect when the user redeems (or auto-redeem runs), so a new redemption rate also applies to positions already held.",
  "feePlans.publish.redemptionRises": "This redemption rate is higher than the current GOLD version's. It applies to every redemption from its effective time, including positions users already hold and auto-redeem, not only to orders placed after it.",
  "feePlans.publish.precision": "Rates are sent as whole numbers at precision {precision}: 1% is 10000.",
  "feePlans.publish.confirm": "Publish version",
  "feePlans.retireDialog.title": "Retire {name}?",
  "feePlans.retireDialog.body": "Retiring is final: the plan takes no new version or assignment from then on.",
  "feePlans.retireDialog.warning": "A retired plan refuses every order and every GOLD redemption (manual and auto-redeem, positions already held included) under a scope that still names it, and fee rules do not fall back. So retiring is refused (BROKER_FEE_PLAN_IN_USE) while a rule names the plan at or after the retire time: reassign or clear those scopes first. Accepted orders keep the maker and taker rates they locked; a redemption is priced when the user redeems.",
  "feePlans.retireDialog.when": "Retires",
  "feePlans.retireDialog.confirm": "Retire plan",
  "feePlans.tabs": "Plan management sections",
  "feePlans.tab.feePlans": "Fee plans",
  "feePlans.tab.feeAssignments": "Fee assignments",
  "nodePicker.change": "Change",
  "nodePicker.browse": "Nodes of the tree",
  "nodePicker.pick": "Choose {name}",
  "scopePicker.scope": "Scope",
  "scopePicker.scope.GROUP": "Group",
  "scopePicker.scope.BRANCH": "Branch",
  "scopePicker.scope.NODE": "Node",
  "scopePicker.explain.GROUP": "The whole tree: every node that no nearer BRANCH or NODE rule covers.",
  "scopePicker.explain.BRANCH": "A node and every node below it, unless a nearer BRANCH or a NODE rule covers them.",
  "scopePicker.explain.NODE": "One node's own users only. It outranks every BRANCH rule and the GROUP rule.",
  "scopePicker.target": "Target",
  "scopePicker.groupTarget": "A GROUP rule always targets the root.",
  "feeAssign.title": "Fee assignments",
  "feeAssign.new": "New assignment",
  "feeAssign.historyNote": "The history of fee rules, newest effective time first. It is not what applies now: each node's Fees page shows the plan the server resolves for it.",
  "feeAssign.empty": "No fee assignments yet.",
  "feeAssign.emptyHint": "Without one, orders from this tree's users are refused (NO_ASSIGNMENT).",
  "feeAssign.plan": "Fee plan",
  "feeAssign.cleared": "Cleared",
  "feeAssign.clearedHint": "No rule at this scope from then",
  "feeAssign.dialogTitle": "Assign a fee plan or clear a rule",
  "feeAssign.dialogBody": "An assignment is immutable once saved, and it never reprices an accepted order.",
  "feeAssign.confirm": "Assign plan",
  "feeAssign.confirmClear": "Clear rule",
  "feeAssign.choosePlan": "Choose a plan…",
  "feeAssign.planRetiring": "{name} (retires {time})",
  "feeAssign.clearOption": "Clear the rule: no fee plan at this scope",
  "feeAssign.planHint": "Only plans that are not retired at the effective time are offered.",
  "feeAssign.fromNow": "now",
  "feeAssign.clearWarning": "From {at}, this scope has no fee rule: its nodes fall back to the next rule up the path (the nearest BRANCH, then GROUP), and orders and GOLD redemptions (manual and auto-redeem) are refused (NO_ASSIGNMENT) where no rule remains.",
  "feeAssign.coverageNone": "{currency}: this plan has no {currency} version in effect at that time. Orders in {currency} under this rule are refused (NO_VERSION_IN_CURRENCY) and do not fall back to another rule.",
  "feeAssign.coverageLater": "{currency}: this plan's first {currency} version takes effect only at {time}. Until then, orders in {currency} under this rule are refused (NO_VERSION_IN_CURRENCY) and do not fall back to another rule.",
  "feeAssign.order": "Which rule applies to an order: a NODE rule on the servicing node, then the nearest BRANCH rule on its path (a BRANCH on the root outranks GROUP), then the GROUP rule.",
  "feeAssign.clearFallsBack": "Clearing removes this scope's rule from its effective time, so resolution falls back up the path.",
  "feeAssign.noFallback": "A rule whose plan is retired, or has no version in a currency, refuses orders in that currency and, in GOLD, redemptions too (manual and auto-redeem): fee rules never skip to a looser rule.",
  "feePlans.tab.sharePlans": "Revenue-share plans",
  "feePlans.tab.shareAssignments": "Revenue-share assignments",
  "sharePlans.rulesTitle": "How revenue-share plans work",
  "sharePlans.rule.shape": "A version splits the broker commission: each share is 1 to 10,000 basis points, the shares total exactly 10,000 (100%), no recipient appears twice, and the remainder recipient holds a share.",
  "sharePlans.rule.recipients": "A recipient is a node of this tree, or the servicing broker (@servicing): the node that serviced the order.",
  "sharePlans.rule.path": "A named recipient must be the assignment's target or a node above it, so that it is on the path of every order the split applies to (OFF_PATH). A version with a servicing-broker share cannot also name its target (SERVICING_NAMED_TWICE).",
  "sharePlans.rule.fallback": "Share rules fall back, fee rules do not: a retired share plan, or one with no version in effect yet, is skipped, and resolution goes on to the next rule up the path or to the default split, 100% to the servicing broker.",
  "sharePlans.rule.immutable": "Versions and assignments are never edited, and an accepted order keeps the split it locked.",
  "sharePlans.list": "Revenue-share plans",
  "sharePlans.newPlan": "New share plan",
  "sharePlans.empty": "No revenue-share plans yet.",
  "sharePlans.emptyHint": "Without one, every order uses the default split: 100% to the servicing broker.",
  "sharePlans.currentVersion": "Current version",
  "sharePlans.noVersionYet": "None yet",
  "sharePlans.detailTitle": "Revenue-share plan: {name}",
  "sharePlans.retiredBanner": "This plan is retired. Scopes assigned to it skip it, and orders fall back to the next rule or the default split.",
  "sharePlans.retiringBanner": "This plan retires at {time}. From then, scopes assigned to it skip it, and orders fall back to the next rule or the default split.",
  "sharePlans.noCurrentBanner": "No version is in effect yet: until one is, rules naming this plan are skipped and orders fall back.",
  "sharePlans.noVersions": "No version yet. Publish one to give the plan a split.",
  "sharePlans.versions": "Versions",
  "sharePlans.shares": "Shares",
  "sharePlans.create.title": "New revenue-share plan",
  "sharePlans.create.body": "The plan starts empty: its split comes with its first version. Nothing applies until the plan is assigned.",
  "sharePlans.publish.title": "Publish a version of {name}",
  "sharePlans.publish.body": "A published version is never edited. The draft starts from the plan's latest version; accepted orders keep the split they locked.",
  "sharePlans.retireWarning": "Retiring is final. From then, every scope assigned to this plan skips it: resolution falls back to the next rule up the path, or to the default split, 100% to the servicing broker. Accepted orders keep the split they locked; a redemption fee is split with the version in effect when it is redeemed.",
  "shares.editor": "Shares",
  "shares.recipient": "Recipient",
  "shares.shareBps": "Share (bps)",
  "shares.percent": "Percent",
  "shares.servicing": "Servicing broker (@servicing)",
  "shares.bpsFor": "Share of {name} in basis points",
  "shares.remove": "Remove",
  "shares.removeFor": "Remove {name}",
  "shares.rowError.empty": "Enter a share.",
  "shares.rowError.invalid": "Whole basis points only.",
  "shares.rowError.range": "From 1 to 10,000 bps.",
  "shares.rowError.repeated": "This recipient is already in the split.",
  "shares.total": "Total: {total} of {max} bps ({percent}).",
  "shares.totalMustBe": "The shares must total exactly {max} bps.",
  "shares.addNode": "Add a node",
  "shares.addServicing": "Add the servicing broker",
  "shares.pickRecipient": "Recipient node",
  "shares.pickHint": "A node of this tree. A closed node cannot receive a share.",
  "shares.remainder": "Remainder recipient",
  "shares.chooseRemainder": "Choose a recipient…",
  "shares.remainderMissing": "Choose a remainder recipient that holds a share.",
  "shares.remainderHint": "Takes what rounding leaves over on each split; it must hold a share.",
  "shares.belowRoot": "{names}: below the root. A version naming them can only be assigned where each is on the path of every order: to that node or a node under it, at BRANCH or NODE scope. A GROUP rule, or a target elsewhere in the tree, is refused (OFF_PATH).",
  "shares.servicingBesideNamed": "This version holds a servicing-broker share beside named nodes, so it cannot be assigned to a node it names: on that node's own orders both are the same broker (SERVICING_NAMED_TWICE).",
  "shareAssign.title": "Revenue-share assignments",
  "shareAssign.plan": "Revenue-share plan",
  "shareAssign.historyNote": "The history of share rules, newest effective time first. It is not what applies now: each node's Fees page shows the split the server resolves for it.",
  "shareAssign.empty": "No revenue-share assignments yet.",
  "shareAssign.emptyHint": "Every order uses the default split: 100% to the servicing broker.",
  "shareAssign.dialogTitle": "Assign a revenue-share plan or clear a rule",
  "shareAssign.dialogBody": "An assignment is immutable once saved, and it never re-splits an accepted order.",
  "shareAssign.clearOption": "Clear the rule: no share plan at this scope",
  "shareAssign.clearWarning": "From {at}, this scope has no share rule: orders fall back to the next rule up the path, or to the default split, 100% to the servicing broker.",
  "shareAssign.noVersion": "This plan has no version in effect at that time: the rule is skipped and orders fall back to the next rule up the path, or to the default split (100% to the servicing broker), until a version takes effect.",
  "shareAssign.noVersionUntil": "This plan's first version takes effect only at {time}. Until then the rule is skipped and orders fall back to the next rule up the path, or to the default split (100% to the servicing broker).",
  "shareAssign.planRetires": "This plan retires at {time}. From then the rule is skipped and orders fall back to the next rule up the path, or to the default split.",
  "shareAssign.order": "Which share rule applies to an order: a NODE rule on the servicing node, then the nearest BRANCH rule on its path (a BRANCH on the root outranks GROUP), then the GROUP rule.",
  "shareAssign.fallback": "Share rules fall back: a cleared scope, a retired plan or a plan with no version in effect is skipped, down to the default split.",
  "shareAssign.path": "Every named recipient of the plan's current and scheduled versions must be the target or a node above it (OFF_PATH), and a version with a servicing-broker share cannot name the target (SERVICING_NAMED_TWICE); otherwise the server refuses the assignment.",

  // --- funding ---
  "funding.title": "Funding",
  "funding.subtitle": "This node's pool, clearing and transfers. Every balance is read live from the ledger.",
  "funding.transferNeedsRole": "Moving pool funds needs an owner or finance membership on this node itself.",
  "funding.pool": "Pool",
  "funding.noPool": "This node holds no {currency} pool.",
  "funding.noPoolHint": "The node's trading modes decide which currencies it holds.",
  "funding.available": "Available",
  "funding.reserved": "Reserved",
  "funding.pendingWithdrawal": "Pending withdrawal",
  "funding.canSpendReceive": "Can spend / receive",
  "funding.canSpendReceiveValue": "{spend} / {receive}",
  "funding.clearing": "Clearing balance",
  "funding.transfers": "Transfers",
  "funding.noTransfers": "No transfers in this currency.",
  "funding.kind": "Kind",
  "funding.fromTo": "From → to",
  "funding.amount": "Amount",
  "funding.actor": "Initiated by",
  "funding.operation": "Operation",
  "funding.poolAvailable": "Pool available",
  "funding.poolReserved": "Pool reserved",
  "funding.allocatedToUsers": "Allocated to users (net)",
  "funding.returnable": "Returnable to parent",
  "transfer.open": "Transfer pool funds",
  "transfer.title": "Pool transfer",
  "transfer.body": "Moves this node's pool across one direct edge: to a direct child, or back to the parent.",
  "transfer.available": "Available in this pool:",
  "transfer.direction": "Direction",
  "transfer.allocate": "Allocate to a direct child",
  "transfer.return": "Return to the parent",
  "transfer.destination": "Destination",
  "transfer.parent": "Parent {id}",
  "transfer.noParent": "This node has no parent: a root pays out through the master rail instead.",
  "transfer.noChildren": "This node has no active direct children.",
  "transfer.amount": "Amount ({currency})",
  "transfer.amountHint": "At most 4 decimals.",
  "transfer.amountError.empty": "Enter an amount.",
  "transfer.amountError.invalid": "Enter a number.",
  "transfer.amountError.tooPrecise": "At most 4 decimals.",
  "transfer.amountError.notPositive": "Must be more than zero.",
  "transfer.reference": "External reference (optional)",
  "transfer.submit": "Transfer",
  "transfer.retrySame": "Check again with the same request",
  "transfer.closeLocked": "Close — check the transfers list",
  "transfer.lockedBody": "The outcome of this transfer is not known yet. Only the identical request can be sent again: the server answers it with the original transfer instead of moving money twice.",
  "transfer.requestKey": "Request key {key}",
  "transfer.replayed": "Replayed — the original transfer",
  "transfer.transferId": "Transfer",
  "transfer.operationId": "Operation",
  "transfer.outcome.committed": "Committed: the funds moved.",
  "transfer.outcome.refused": "Not moved. The transfer's record keeps the reason.",
  "transfer.outcome.unknown": "Pending: the ledger has not confirmed yet. Do not start a new transfer; check again with the same request.",

  // --- revenue ---
  "revenue.title": "Revenue",
  "revenue.subtitle": "This node's own revenue by state, by period and by earning.",
  "revenue.balances": "Revenue by state",
  "revenue.noBalance": "No revenue in {currency}.",
  "revenue.state.pending": "Pending",
  "revenue.state.held": "Held",
  "revenue.state.available": "Available",
  "revenue.state.payoutReserved": "Reserved for payout",
  "revenue.state.paid": "Paid",
  "revenue.state.unpaid": "Unpaid in total",
  "revenue.pendingSub": "Not yet receivable",
  "revenue.availableSub": "Can be paid in a batch the exchange prepares",
  "revenue.heldSub": "Kept out of payouts by a hold",
  "revenue.reservedSub": "In a payout batch",
  "revenue.unpaidSub": "Pending, held, available and reserved",
  "revenue.pendingExplained": "Pending revenue is not available yet: it becomes available when the revenue reconciliation has matched it against the ledger.",
  "revenue.latestRecon": "Latest revenue reconciliation: {state},",
  "revenue.noRecon": "No revenue reconciliation has run yet.",
  "revenue.periods": "Earnings by period",
  "revenue.window": "Window",
  "revenue.kind": "Kind",
  "revenue.total": "Total",
  "revenue.today": "Today",
  "revenue.current": "Current period",
  "revenue.previous": "Previous period",
  "revenue.earned": "Earned",
  "revenue.sharesReceived": "Shares received",
  "revenue.periodsNote": "Earned: sources this node services itself. Shares received: sources a descendant services. Today is the Asia/Bangkok day; periods are calendar months (UTC).",
  "revenue.earnings": "Earnings",
  "revenue.noEarnings": "No earnings match.",
  "revenue.origin": "Origin",
  "revenue.role": "Role",
  "revenue.source": "Source",
  "revenue.share": "Share",
  "revenue.amount": "Amount",
  "revenue.held": "Held",

  // --- payouts ---
  "payouts.title": "Payouts",
  "payouts.subtitle": "Payout batches for this node. The exchange reviews, approves and pays every batch.",
  "payouts.manualNote": "The exchange prepares each payout by hand when it settles with you.",
  "payouts.settlementLink": "Your net position and payment requests are on Settlement.",
  "payouts.cutoff": "Cutoff",
  "payouts.heldNote": "Held earnings are kept out of every batch until the hold is released. Held now:",
  "payouts.batches": "Batches",
  "payouts.none": "No payout batches in this currency.",
  "payouts.nextAction": "What happens next",
  "payouts.total": "Total",
  "payouts.items": "Earnings",
  "payouts.batch": "Batch",
  "payouts.failureReason": "Reason: {reason}",
  "payouts.state.DRAFT": "Draft",
  "payouts.state.PENDING_REVIEW": "Awaiting review",
  "payouts.state.APPROVED": "Approved",
  "payouts.state.PROCESSING": "Processing",
  "payouts.state.PAID": "Paid",
  "payouts.state.FAILED": "Failed",
  "payouts.state.REJECTED": "Rejected",
  "payouts.state.CANCELLED": "Cancelled",
  "payouts.next.DRAFT": "Being prepared. The exchange submits it for review; its earnings are already reserved.",
  "payouts.next.PENDING_REVIEW": "Waiting for the exchange's finance review. Nothing for you to do.",
  "payouts.next.APPROVED": "Approved. The exchange will pay it outside Smart Market.",
  "payouts.next.PROCESSING": "Payment sent; waiting for the rail to confirm it.",
  "payouts.next.PAID": "Paid. Nothing left to do.",
  "payouts.next.FAILED": "The payment failed. Its earnings are back in available revenue and go into the next batch; check the payment details with the exchange.",
  "payouts.next.REJECTED": "Rejected by the exchange's review. Its earnings are back in available revenue.",
  "payouts.next.CANCELLED": "Cancelled. Its earnings are back in available revenue.",
  "payouts.next.unknown": "A state this portal does not know yet.",

  // --- statements ---
  "statements.title": "Statements",
  "statements.subtitle": "Revenue statement for a period, with every supporting movement.",
  "statements.exportCsv": "Export CSV",
  "statements.period": "Period",
  "statements.thisMonth": "This month",
  "statements.lastMonth": "Last month",
  "statements.fromDay": "From",
  "statements.toDay": "To (inclusive)",
  "statements.apply": "Apply",
  "statements.badRange": "Choose an end date on or after the start date.",
  "statements.scope": "Whose revenue",
  "statements.scopeNode": "This node",
  "statements.scopeSubtree": "This node and descendants",
  "statements.windowDays": "Window: {from} to {to} inclusive, whole days in UTC",
  "statements.recon": "Latest revenue reconciliation: {state}",
  "statements.generated": "generated",
  "statements.noActivity": "No revenue in this currency for this period.",
  "statements.arithmetic": "Statement",
  "statements.arithmeticNote": "Opening + earned + shares received + adjustments + reassigned in − reassigned out − payouts = closing. Every line is the server's.",
  "statements.opening": "Opening balance",
  "statements.earnedMaker": "Earned · maker",
  "statements.earnedTaker": "Earned · taker",
  "statements.earnedRedemption": "Earned · redemption",
  "statements.sharesMaker": "Shares received · maker",
  "statements.sharesTaker": "Shares received · taker",
  "statements.sharesRedemption": "Shares received · redemption",
  "statements.adjustments": "Adjustments",
  "statements.reassignedIn": "Reassigned in",
  "statements.reassignedOut": "Reassigned out",
  "statements.payouts": "Payouts",
  "statements.closing": "Closing balance",
  "statements.closingBreakdown": "Closing balance by state",
  "statements.info": "For information",
  "statements.infoNote": "Totals over the supporting sources; void commission was never collected and is not money.",
  "statements.tradedValue": "Traded value",
  "statements.exchangeFee": "Exchange fee",
  "statements.brokerCommission": "Broker commission",
  "statements.voidCommission": "Void commission",
  "statements.rows": "Supporting movements",
  "statements.noRows": "No movements in this period.",
  "statements.rowKind": "Kind",
  "statements.recipient": "Recipient",
  "statements.sourceOrRef": "Source or reference",
  "statements.effect": "Effect on balance",

  // --- reconciliation ---
  "recon.title": "Reconciliation",
  "recon.subtitle": "The latest funding and revenue reconciliation runs, trimmed to your scope.",
  "recon.funding": "Funding reconciliation",
  "recon.revenue": "Revenue reconciliation",
  "recon.noFundingRun": "No funding reconciliation has run yet.",
  "recon.noRevenueRun": "No revenue reconciliation has run yet.",
  "recon.started": "Started",
  "recon.finished": "finished",
  "recon.runError": "The run could not read a source: {error}",
  "recon.noRootsInCurrency": "No {currency} figures in this run.",
  "recon.noItemsInCurrency": "No {currency} nodes in this run.",
  "recon.root": "Root",
  "recon.topUps": "Top-ups",
  "recon.payoutsCompleted": "Payouts completed",
  "recon.nodeTotal": "Node total",
  "recon.userAllocations": "User allocations",
  "recon.userReturns": "User returns",
  "recon.balanced": "Balanced",
  "recon.findings": "Findings",
  "recon.noFindings": "No findings in your scope.",
  "recon.rule": "Rule",
  "recon.severity": "Severity",
  "recon.node": "Node",
  "recon.detail": "Detail",
  "recon.expected": "Expected",
  "recon.actual": "Actual",
  "recon.ledgerPending": "Ledger pending",
  "recon.earningsPending": "Earnings pending",
  "recon.ledgerAvailable": "Ledger available",
  "recon.earningsAvailable": "Earnings available",
  "recon.matched": "Matched",
  "recon.inFlight": "In flight",
  "recon.yes": "Yes",
  "recon.no": "No",
  "recon.cases": "Discrepancy cases",
  "recon.casesAvailable": "Discrepancy cases are available.",
  "recon.casesLater": "Discrepancy cases arrive with phase 08d.",
  "recon.casesLaterHint": "Until then, findings above are the whole picture; no case is shown that does not exist.",
  "recon.caption.fundingSource": "the ledger or the custody rail, for this broker tree",
  "recon.caption.revenueSource": "the ledger's revenue balances",
  "recon.caption.ledgerFinding": "the ledger's funding summary was unreadable",
  "recon.caption.railFinding": "the custody rail's totals were unavailable",
  "settlement.title": "Settlement",
  "settlement.subtitle": "Who owes whom, and how much, over a recent period. Read-only: settlements are recorded by the exchange.",
  "settlement.period": "Period",
  "settlement.window.1": "Last 24 hours",
  "settlement.window.3": "Last 3 days",
  "settlement.window.7": "Last 7 days",
  "settlement.window.14": "Last 14 days",
  "settlement.window.30": "Last 30 days",
  "settlement.scope": "Figures",
  "settlement.scope.subtree": "This node and its branches",
  "settlement.scope.node": "This node alone",
  "settlement.outstanding": "Net position (information only)",
  "settlement.dir.WE_OWE_BROKER": "We owe you",
  "settlement.dir.BROKER_OWES_US": "You owe us",
  "settlement.dir.SETTLED": "Settled — nothing owed either way",
  "settlement.ifPendingClear": "If pending settlements clear",
  "settlement.empty": "No settlement activity yet",
  "settlement.emptyHint": "Nothing has been earned, traded against your clearing account or recorded in this currency.",
  "settlement.transferIn": "Uncovered after collateral (information only)",
  "settlement.covered": "Your collateral covers the position",
  "settlement.periodTitle": "This period",
  "settlement.sinceLaunch": "Since launch, as of the period end",
  "settlement.revenueReleased": "Revenue that became available",
  "settlement.revenuePaid": "Revenue already paid to you",
  "settlement.clearingMovement": "Clearing movement",
  "settlement.periodNet": "Net for the period",
  "settlement.revenueAvailable": "Revenue available, not yet paid",
  "settlement.clearingBalance": "Clearing balance",
  "settlement.noClearing": "No clearing account — your position is revenue only",
  "settlement.gross": "Gross position",
  "settlement.confirmedIn": "Settlements you paid us, confirmed",
  "settlement.confirmedOut": "Settlements we paid you, confirmed",
  "settlement.collateral": "Collateral held",
  "settlement.collateralNote": "Your prefunded pool — shown beside the position, never inside it",
  "settlement.uncovered": "After collateral",
  "settlement.fees": "Fees in this period",
  "settlement.tradedValue": "Traded value",
  "settlement.exchangeFee": "Exchange fee",
  "settlement.brokerCommission": "Broker commission",
  "settlement.branches": "Branches",
  "settlement.branchesHint": "Each child node with its own branches. Select one to drill down.",
  "settlement.thisNode": "This node alone",
  "settlement.open": "Open",
  "settlement.noActivity": "No activity yet",
  "settlement.notes": "Settlement notes (memo)",
  "settlement.notesEmpty": "No settlement recorded in this currency.",
  "settlement.noteDir.INBOUND": "You paid us",
  "settlement.noteDir.OUTBOUND": "We paid you",
  "settlement.noteStatus.PENDING": "Pending — receipt not yet confirmed",
  "settlement.noteStatus.CONFIRMED": "Confirmed",
  "settlement.noteStatus.SUPERSEDED": "Superseded",
  "settlement.valueDate": "Value date",
  "settlement.reference": "Reference",
  "settlement.recordedAt": "Recorded",
  "settlement.direction": "Who owes whom",
  "settlement.externalCredit": "External credit owed",
  "settlement.externalCreditHint": "What a payment request asks for: your users' external credit balance net of confirmed settlements. Shown apart from the net position, never added to it.",
  "settlement.revenueInPayout": "Commission in open payout batches",
  "settlement.usersOpenCost": "Users' open positions",
  "settlement.usersSettled": "Users' settled results and fees",
  "settlement.clearingPart": "Part of the clearing balance, never added to it",
  "settlement.requestsOpen": "Open payment requests",
  "settlement.requestsOpenValue": "{count} open · outstanding",
  "settlement.openReceivable": "Open receivable",
  "settlement.receivableState.OPEN": "Open",
  "settlement.receivableState.ACKNOWLEDGED": "Acknowledged",
  "settlement.requests": "Payment requests",
  "settlement.requestsHint": "What the exchange has asked you to pay: the external credit owed, never the net position. Read-only: the exchange records and cancels requests, and a request is paid when your confirmed settlements cover it.",
  "settlement.requestsFilter": "Show",
  "settlement.requestsFilter.open": "Open",
  "settlement.requestsFilter.all": "All",
  "settlement.requestsEmptyOpen": "No open payment requests.",
  "settlement.requestsEmpty": "No payment requests.",
  "settlement.requestNode": "Node",
  "settlement.requestThisNode": "This node",
  "settlement.requested": "Requested",
  "settlement.received": "Received",
  "settlement.requestOutstanding": "Outstanding",
  "settlement.dueDate": "Due date",
  "settlement.requestNote": "Note",
  "settlement.requestedAt": "Requested at",
  "settlement.overdue": "Overdue",
  "settlement.requestStatus.REQUESTED": "Requested",
  "settlement.requestStatus.PAID": "Paid",
  "settlement.requestStatus.CANCELLED": "Cancelled",
  "settlement.paidOn": "Paid on",
  "settlement.cancelReason": "Reason: {reason}",
  "settlement.openRequestsColumn": "Open requests",
  "settlement.noteForRequest": "For payment request {id}",

  // --- SM-941: server captions, finding details, enums ---
  "settlement.caption.outstanding": "Net position, information only, never sent or asked for as one amount.",
  "settlement.caption.noteMemo": "Memo: a record of an external bank transfer, not funds received. It moves no balance, collateral or headroom.",
  "settlement.caption.noteInbound": "Record only — this does not top up the broker's pool; headroom changes only with a real master-wallet top-up.",
  "settlement.caption.noteOutbound": "Record of a manual finance action — no payout batch was created and no revenue was marked paid.",
  "settlement.caption.paymentRequest": "Request for external credit the broker owes, never the net position. It moves no money; confirmed inbound settlements mark it paid.",
  "settlement.externalCreditSubtree": "Total of this node and its branches. Each payment request asks one node for its own figure, never for this total.",
  "clearing.beforeSettlements": "The ledger's clearing position. Settlement notes and outside payments never post to the ledger, so they never lower it.",
  "clearing.seeSettlement": "See what is still owed in Settlement",
  "reconDetail.LEDGER_OTHER_CURRENCY": "The ledger answered an account in another currency.",
  "reconDetail.LEDGER_UNREADABLE": "The ledger's funding summary was unreadable.",
  "reconDetail.LEDGER_UNAVAILABLE": "The ledger's funding summary was unavailable, so this tree was not evaluated.",
  "reconDetail.ROOT_UNBALANCED": "Top-ups less completed payouts do not equal the node totals plus user allocations less user returns.",
  "reconDetail.NODE_TOTAL_UNEXPLAINED": "The node's total (available, reserved and pending withdrawal) is not explained by its funding movements.",
  "reconDetail.OPERATION_LEG_COUNT": "A funding operation must have exactly two legs.",
  "reconDetail.OPERATION_LEGS_DIFFER": "The operation's outgoing and incoming legs differ.",
  "reconDetail.OPERATION_OTHER_CURRENCY": "The operation's legs are in another currency.",
  "reconDetail.POOL_NO_LEDGER_ACCOUNT": "A pool in the registry has no ledger account.",
  "reconDetail.POOL_WALLET_MISMATCH": "The registry records another wallet than the ledger holds.",
  "reconDetail.COMMITTED_NO_OPERATION": "A committed transfer has no ledger operation.",
  "reconDetail.PAIR_AMOUNT_DIFFERS": "The ledger's amount differs from the registry's.",
  "reconDetail.PAIR_SOURCE_KIND_DIFFERS": "The ledger records another kind of movement than the registry.",
  "reconDetail.PAIR_ACCOUNTS_DIFFER": "The ledger moved money between other accounts than the registry names.",
  "reconDetail.FAILED_HAS_LEGS": "A failed transfer has ledger legs.",
  "reconDetail.PENDING_TOO_OLD": "A pending transfer is older than {minutes} minutes.",
  "reconDetail.TRANSFER_MANUAL_REVIEW": "A transfer awaits manual review after repeated unclear ledger outcomes.",
  "reconDetail.OPERATION_NO_REGISTRY_ROW": "A ledger funding operation has no registry record.",
  "reconDetail.RAIL_UNAVAILABLE": "The custody rail's totals were unavailable.",
  "reconDetail.RAIL_UNREADABLE": "The custody rail's totals were unreadable.",
  "reconDetail.RAIL_DEPOSITS_DIFFER": "Credited deposits differ from the master pool's crypto top-ups.",
  "reconDetail.RAIL_WITHDRAWALS_DIFFER": "Completed withdrawals differ from the master pool's completed crypto payouts.",
  "reconDetail.RAIL_OPEN_WITHDRAWALS_DIFFER": "Open withdrawals differ from the master pool's pending withdrawals.",
  "reconDetail.PAYOUT_HOLD_TOO_OLD": "A master pool payout hold has been open for longer than {hours} hours.",
  "reconDetail.OTHER_LEG_KIND": "The ledger holds movements of another kind on a broker account.",
  "reconDetail.MANUAL_RAIL_NON_ROOT": "Manual minting or burning on a pool that is not the root's.",
  "reconDetail.CRYPTO_RAIL_NON_ROOT": "Crypto top-up or payout movements on a pool that is not the root's.",
  "reconSeverity.ERROR": "Error",
  "reconSeverity.MANUAL_REVIEW": "Manual review",
  "accountEvent.CREATE": "Account created",
  "accountEvent.SUSPEND": "Suspended",
  "accountEvent.RESUME": "Resumed",
  "accountEvent.CLOSE": "Closed",
  "auditEntity.BROKER": "Broker",
  "auditEntity.SETTINGS": "Settings",
  "auditEntity.MEMBERSHIP": "Staff membership",
  "auditEntity.HIERARCHY": "Hierarchy",
  "auditEntity.PAYOUT_SETTING": "Payout setting",
  "auditEntity.PAYOUT_DESTINATION": "Payout destination",
  "auditEntity.CLEARING_ACCOUNT": "Clearing account",
  "auditEntity.FEE_PLAN": "Fee plan",
  "auditEntity.FEE_PLAN_VERSION": "Fee plan version",
  "auditEntity.FEE_ASSIGNMENT": "Fee assignment",
  "auditEntity.EXPOSURE_LIMIT": "Exposure limit",
  "auditEntity.EXPOSURE_LIMIT_REQUEST": "Exposure limit request",
  "auditEntity.RECEIVABLE": "Receivable",
  "auditEntity.RECEIVABLE_ACTION": "Receivable action",
  "auditEntity.POOL": "Pool",
  "auditEntity.FUNDING_TRANSFER": "Funding transfer",
  "auditEntity.CREDENTIAL": "Server credential",
  "auditEntity.PAYOUT_BATCH": "Payout batch",
  "auditEntity.PROVIDER_CONFIG": "Provider configuration",
  "auditEntity.PAYMENT_REQUEST": "Payment request",
  "auditEntity.REVENUE_SHARE_PLAN": "Revenue share plan",
  "auditEntity.REVENUE_SHARE_VERSION": "Revenue share version",
  "auditEntity.REVENUE_SHARE_ASSIGNMENT": "Revenue share assignment",
  "auditEntity.SETTLEMENT_NOTE": "Settlement note",
  "auditEntity.CURRENCY_IMAGE": "Currency image",
  "auditEntity.REVENUE_ADJUSTMENT": "Revenue adjustment",
  "auditEntity.REVENUE_REASSIGNMENT": "Revenue reassignment",
  "auditEntity.REVENUE_HOLD": "Revenue hold",
  "cancelReason.USER_REQUESTED": "Cancelled by the user",
  "cancelReason.SELF_MATCH_PREVENTED": "Would have matched the user's own order",
  "cancelReason.NO_LIQUIDITY": "No liquidity",
  "cancelReason.INSUFFICIENT_LIQUIDITY": "Not enough liquidity",
  "cancelReason.EXPIRED": "Expired",
  "cancelReason.MARKET_CLOSED": "Market closed",
  "cancelReason.FAK_REMAINDER": "Rest not filled at once",
  "cancelReason.INVALID_PRICE": "Invalid price",
  "cancelReason.INVALID_QUANTITY": "Invalid quantity",
  "cancelReason.PRICE_OUT_OF_RANGE": "Price out of range",
  "cancelReason.TICK_VIOLATION": "Price not on a tick",
  "cancelReason.PRECISION_VIOLATION": "Too many decimal places",
  "cancelReason.MIN_AMOUNT_VIOLATION": "Below the minimum amount",
  "cancelReason.INVALID_COMBO": "Invalid order combination",
  "cancelReason.FILLED": "Filled",
  "cancelReason.CANCELLED_BEFORE_ACTIVATION": "Cancelled before its funding cleared",
  "cancelReason.RESOLUTION_UNFILLABLE": "Market ended before its funding cleared",
  "cancelReason.STRANDED_BEFORE_ENGINE": "Never reached the market",
  "cancelReason.ORDER_000101": "Invalid order request",
  "cancelReason.ORDER_000102": "Invalid price type",
  "cancelReason.ORDER_000103": "Invalid time in force",
  "cancelReason.ORDER_000104": "Invalid side",
  "cancelReason.ORDER_000105": "No price on a limit order",
  "cancelReason.ORDER_000106": "No expiry on a good-till-date order",
  "cancelReason.ORDER_000107": "Invalid order combination",
  "cancelReason.ORDER_000108": "Invalid quantity",
  "cancelReason.ORDER_000109": "Invalid price",
  "cancelReason.ORDER_000110": "Duplicate client order id",
  "cancelReason.ORDER_000111": "Duplicate command",
  "cancelReason.ORDER_000112": "User account not active",
  "cancelReason.ORDER_000113": "Trading halted on the market",
  "cancelReason.ORDER_000114": "Event not open for trading",
  "cancelReason.ORDER_000115": "Risk limit exceeded",
  "cancelReason.ORDER_000116": "Not enough balance or tickets",
  "cancelReason.ORDER_000117": "Could not be sent to the market",
  "cancelReason.ORDER_000118": "Rejected by the matching engine",
  "cancelReason.ORDER_000119": "No answer from the market",
  "cancelReason.ORDER_000120": "Order not found",
  "cancelReason.ORDER_000121": "Order already finished",
  "cancelReason.ORDER_000122": "Could not be saved",
  "cancelReason.ORDER_000123": "A service was unavailable",
  "cancelReason.ORDER_000124": "Not signed in",
  "cancelReason.ORDER_000125": "Not permitted",
  "cancelReason.ORDER_000126": "Status update not delivered",
  "cancelReason.ORDER_000127": "Outcome does not match the market",
  "cancelReason.ORDER_000128": "Not enough tickets to sell",
  "cancelReason.ORDER_000129": "No outcome chosen",
  "cancelReason.ORDER_000130": "Game trading is off",
  "cancelReason.ORDER_000131": "Mode not available on this market",
  "cancelReason.EXTERNAL_CREDIT_MODE_UNSUPPORTED": "Not available for external-credit accounts",
  "cancelReason.BROKER_EXPOSURE_EXCEEDED": "Over the broker's exposure limit",
  "cancelReason.BROKER_EXPOSURE_UNAVAILABLE": "Exposure headroom could not be read",
  "cancelReason.FUNDING_AUTHORIZATION_MISMATCH": "Funding did not match the order",
  "cancelReason.FUNDING_AUTHORIZATION_STALE": "Funding had already ended",
  "cancelReason.FUNDING_AUTHORIZATION_UNRESOLVED": "Funding was not confirmed",
  "cancelReason.FUNDING_REJECTED": "Funding refused by the provider",
  "auditAction.CREATE": "Created",
  "auditAction.CREATED": "Created",
  "auditAction.UPDATE": "Updated",
  "auditAction.UPDATE_SETTINGS": "Settings updated",
  "auditAction.BROKER_CURRENCY_IMAGE_SET": "Currency image changed",
  "auditAction.BROKER_CURRENCY_IMAGE_REMOVED": "Currency image removed",
  "auditAction.APPROVE": "Approved",
  "auditAction.APPROVED": "Approved",
  "auditAction.APPROVAL": "Approval recorded",
  "auditAction.PAUSE": "Paused",
  "auditAction.RESUME": "Resumed",
  "auditAction.BEGIN_CLOSE": "Closing started",
  "auditAction.CLOSE": "Closed",
  "auditAction.MOVE": "Moved in the hierarchy",
  "auditAction.ENSURE": "Set up",
  "auditAction.ISSUE": "Issued",
  "auditAction.ROTATE": "Rotated",
  "auditAction.REVOKE": "Revoked",
  "auditAction.INVITE": "Invited",
  "auditAction.ACTIVATE": "Activated",
  "auditAction.SUSPEND": "Suspended",
  "auditAction.REMOVE": "Removed",
  "auditAction.CHANGE_ROLE": "Role changed",
  "auditAction.PENDING": "Pending",
  "auditAction.COMMITTED": "Committed",
  "auditAction.FAILED": "Failed",
  "auditAction.MANUAL_REVIEW": "Sent to manual review",
  "auditAction.REQUEST": "Requested",
  "auditAction.REJECT": "Rejected",
  "auditAction.REJECTED": "Rejected",
  "auditAction.CANCEL": "Cancelled",
  "auditAction.CANCELLED": "Cancelled",
  "auditAction.APPLY": "Applied",
  "auditAction.APPLIED": "Applied",
  "auditAction.POSTED": "Posted",
  "auditAction.PUBLISH": "Published",
  "auditAction.RETIRE": "Retired",
  "auditAction.ASSIGN": "Assigned",
  "auditAction.CLEAR": "Assignment cleared",
  "auditAction.ESCROWED": "Held in escrow",
  "auditAction.ESCROW_REFUSED": "Escrow refused",
  "auditAction.EXECUTED": "Executed",
  "auditAction.PAID": "Paid",
  "auditAction.RESTORED": "Restored",
  "auditAction.HOLD": "Held",
  "auditAction.RELEASE_HOLD": "Hold released",
  "auditAction.RECORD": "Recorded",
  "auditAction.CONFIRM": "Confirmed",
  "auditAction.OPEN": "Opened",
  "auditAction.ACKNOWLEDGE": "Acknowledged",
  "auditAction.SETTLED": "Settled",
  "auditAction.WRITTEN_OFF": "Written off",
  "auditAction.REQUEST_SETTLE": "Settlement requested",
  "auditAction.REQUEST_WRITE_OFF": "Write-off requested",
  "auditAction.RESEND_INVITE": "Invitation sent again",
  "auditAction.STATEMENT_EXPORTED": "Statement exported",
  "orders.reasonOther": "Other reason",
  "settlement.refresh": "Refresh",
  "transfer.failed": "The transfer failed.",
  "poolStatus.ACTIVE": "Active",
  "poolStatus.FROZEN": "Frozen",
  "poolStatus.CLOSED": "Closed",
} as const;

export type TranslationKey = keyof typeof en;
